import { useStore } from 'dg-web-shared/lib/Flux';
import { StaticPageLayout } from '../../../layout/utils/StaticPageLayout';
import { TwoColumnsProperties } from '../../../layout/utils/TwoColumnsProperties';
import { ResponsiveProperties } from '../../../layout/utils/ResponsiveProperties';
import { columnsVariant } from '../../../layout/utils/columnsVariant';
import * as LayoutState from '../../../layout/state/LayoutState';
import { Layout } from '../../../layout/components/Layout';
import {
    LayoutContainer,
    LayoutVariantOption,
} from '../../../layout/components/LayoutContainer';
import { LayoutBackground } from '../../../layout/components/LayoutBackground';
import { ColumnBlock } from '../../../layout/components/ColumnBlock';
import { ColumnContainer } from '../../../layout/components/ColumnContainer';
import { HeaderLargeBlock } from '../../../layout/components/header/HeaderLargeBlock';
import { LogoSlogan } from '../../../layout/components/header/logo-slogan/LogoSlogan';
import { css } from '@emotion/css';
import { Colors } from 'dg-web-shared/ui/vars';
import { Typo } from 'dg-web-shared/ui/typo.ts';
import React from 'react';

export interface StaticPageProps {
    children?: React.ReactNode;
    hideBackground?: boolean;
}

export function StaticPage({ children, hideBackground }: StaticPageProps) {
    return (
        <Layout>
            <LayoutContainer layoutVariant={LayoutVariantOption.StaticPage}>
                {!hideBackground && (
                    <LayoutBackground
                        layoutVariant={LayoutVariantOption.StaticPage}
                    />
                )}
                <HeaderLargeBlock />
                <LogoSlogan layoutVariant={LayoutVariantOption.StaticPage} />
                <ColumnContainer
                    relative
                    layoutVariant={LayoutVariantOption.StaticPage}
                >
                    {children}
                </ColumnContainer>
            </LayoutContainer>
        </Layout>
    );
}

export interface LeftColumnProps {
    children?: React.ReactNode;
}

export function LeftColumn({ children }: LeftColumnProps) {
    const { storeState } = useStore(store => ({
        layout: new LayoutState.StateSlice(store).state,
    }));

    const staticPageLayout = new StaticPageLayout(storeState);
    const columnProps = new TwoColumnsProperties(storeState);
    return (
        <ColumnBlock
            width={staticPageLayout.column1Width}
            isEnabled={true}
            hasSpacing={!columnProps.variant.oneColumn}
        >
            <ContentContainer>{children}</ContentContainer>
        </ColumnBlock>
    );
}

export interface RightColumnProps {
    children?: React.ReactNode;
}

export function RightColumn({ children }: RightColumnProps) {
    const { storeState } = useStore(store => ({
        layout: new LayoutState.StateSlice(store).state,
    }));

    const staticPageLayout = new StaticPageLayout(storeState);
    return (
        <ColumnBlock
            width={staticPageLayout.column2Width}
            isEnabled={true}
            hasSpacing={false}
        >
            {children}
        </ColumnBlock>
    );
}

interface ContentContainerProps {
    children?: React.ReactNode;
}

export function ContentContainer({ children }: ContentContainerProps) {
    const { storeState } = useStore(store => ({
        layout: new LayoutState.StateSlice(store).state,
    }));

    const responsiveProps = new ResponsiveProperties(storeState);
    return (
        <div
            className={css([
                {
                    height: '100%',
                    maxWidth: '688px',
                    marginLeft: '104px',
                    userSelect: 'text',
                },
                columnsVariant(storeState) === 'OneColumn' && {
                    paddingBottom: '40px',
                },
                responsiveProps.toString() === 'Mobile' && {
                    margin: 0,
                    maxWidth: '100%',
                    padding: '24px',
                    backgroundColor: Colors.background,
                },
            ])}
        >
            {children}
        </div>
    );
}

interface HeadingLargeProps {
    first?: boolean;
    children?: React.ReactNode;
}

export function HeadingLarge({ first, children }: HeadingLargeProps) {
    return (
        <h2
            className={css([
                {
                    margin: 0,
                    color: Colors.blue,
                    ...Typo.heading3,
                },
                !first && {
                    marginTop: '26px',
                },
            ])}
            data-first={!!first}
        >
            {children}
        </h2>
    );
}
